import React, { createContext, useContext, useState, useEffect } from 'react';
import { Guest, Statistics, RealtimeActivity, NotificationItem } from '../types';
import { mockGuests, mockStatistics, mockRealtimeActivity, mockCompanies } from '../mock/data';

interface AppContextType {
  // Data
  guests: Guest[];
  statistics: Statistics;
  realtimeActivity: RealtimeActivity[];
  notifications: NotificationItem[];
  
  // Actions
  addGuest: (guest: Omit<Guest, 'id' | 'registrationTime' | 'status'>) => void;
  approveGuest: (guestId: string, approvedBy: string) => void;
  rejectGuest: (guestId: string, approvedBy: string) => void;
  issueCard: (guestId: string, cardId: string, floor: number, elevatorZone: 'A' | 'B' | 'C') => void;
  checkInGuest: (guestId: string) => void;
  checkOutGuest: (guestId: string) => void;
  markNotificationAsRead: (notificationId: string) => void;
  addNotification: (notification: Omit<NotificationItem, 'id' | 'time' | 'read'>) => void;
  resetToMockData: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};

// LocalStorage keys
const STORAGE_KEYS = {
  GUESTS: 'tower_manage_guests',
  STATISTICS: 'tower_manage_statistics',
  REALTIME_ACTIVITY: 'tower_manage_realtime_activity',
  NOTIFICATIONS: 'tower_manage_notifications'
};

// Helper functions for localStorage
const loadFromStorage = (key: string, defaultValue: any): any => {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = JSON.parse(stored);
      // Convert date strings back to Date objects for guests
      if (key === STORAGE_KEYS.GUESTS && Array.isArray(parsed)) {
        return parsed.map(guest => ({
          ...guest,
          registrationTime: new Date(guest.registrationTime),
          approvedTime: guest.approvedTime ? new Date(guest.approvedTime) : undefined,
          checkInTime: guest.checkInTime ? new Date(guest.checkInTime) : undefined,
          checkOutTime: guest.checkOutTime ? new Date(guest.checkOutTime) : undefined,
        }));
      }
      // Convert date strings for notifications
      if (key === STORAGE_KEYS.NOTIFICATIONS && Array.isArray(parsed)) {
        return parsed.map(notification => ({
          ...notification,
          time: new Date(notification.time)
        }));
      }
      // Convert date strings for realtime activity
      if (key === STORAGE_KEYS.REALTIME_ACTIVITY && Array.isArray(parsed)) {
        return parsed.map(activity => ({
          ...activity,
          time: new Date(activity.time)
        }));
      }
      return parsed;
    }
  } catch (error) {
    console.error(`Error loading ${key} from localStorage:`, error);
  }
  return defaultValue;
};

const saveToStorage = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error(`Error saving ${key} to localStorage:`, error);
  }
};

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [guests, setGuests] = useState<Guest[]>(() => 
    loadFromStorage(STORAGE_KEYS.GUESTS, mockGuests)
  );
  const [statistics, setStatistics] = useState<Statistics>(() => 
    loadFromStorage(STORAGE_KEYS.STATISTICS, mockStatistics)
  );
  const [realtimeActivity, setRealtimeActivity] = useState<RealtimeActivity[]>(() => 
    loadFromStorage(STORAGE_KEYS.REALTIME_ACTIVITY, mockRealtimeActivity)
  );

  console.log('AppProvider - Initialized with guests:', guests.length);
  console.log('AppProvider - Mock guests:', mockGuests.length);
  
  const [notifications, setNotifications] = useState<NotificationItem[]>(() => 
    loadFromStorage(STORAGE_KEYS.NOTIFICATIONS, [
      {
        id: '1',
        type: 'new-registration',
        title: 'Đăng ký mới',
        message: 'Lê Thị Bình đã đăng ký vào tòa nhà',
        time: new Date(),
        read: false,
        guestId: '2'
      },
      {
        id: '2',
        type: 'approval-needed',
        title: 'Cần duyệt',
        message: 'Phạm Thị Dung chờ duyệt đăng ký',
        time: new Date(),
        read: false,
        guestId: '4'
      }
    ])
  );

  // Generate unique ID
  const generateId = () => Date.now().toString();

  // Save to localStorage when state changes
  useEffect(() => {
    saveToStorage(STORAGE_KEYS.GUESTS, guests);
  }, [guests]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.STATISTICS, statistics);
  }, [statistics]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.REALTIME_ACTIVITY, realtimeActivity);
  }, [realtimeActivity]);

  useEffect(() => {
    saveToStorage(STORAGE_KEYS.NOTIFICATIONS, notifications);
  }, [notifications]);

  // Add new guest
  const addGuest = (guestData: Omit<Guest, 'id' | 'registrationTime' | 'status'>) => {
    const newGuest: Guest = {
      ...guestData,
      id: generateId(),
      registrationTime: new Date(),
      status: 'pending'
    };

    console.log('AppContext - Adding new guest:', newGuest);
    console.log('AppContext - Current guests before add:', guests.length);
    
    setGuests(prev => {
      const updated = [...prev, newGuest];
      console.log('AppContext - Updated guests array:', updated.length);
      return updated;
    });
    
    // Add notification
    addNotification({
      type: 'new-registration',
      title: 'Đăng ký mới',
      message: `${newGuest.fullName} đã đăng ký vào tòa nhà`,
      guestId: newGuest.id
    });

    console.log('AppContext - New guest added successfully:', newGuest);
  };

  // Approve guest
  const approveGuest = (guestId: string, approvedBy: string) => {
    setGuests(prev => prev.map(guest => 
      guest.id === guestId 
        ? { ...guest, status: 'approved' as const, approvedBy, approvedTime: new Date() }
        : guest
    ));

    const guest = guests.find(g => g.id === guestId);
    if (guest) {
      addNotification({
        type: 'approval-needed',
        title: 'Đã duyệt',
        message: `${guest.fullName} đã được duyệt bởi ${approvedBy}`,
        guestId
      });
    }
  };

  // Reject guest
  const rejectGuest = (guestId: string, approvedBy: string) => {
    setGuests(prev => prev.map(guest => 
      guest.id === guestId 
        ? { ...guest, status: 'rejected' as const, approvedBy, approvedTime: new Date() }
        : guest
    ));
  };

  // Issue card to guest
  const issueCard = (guestId: string, cardId: string, floor: number, elevatorZone: 'A' | 'B' | 'C') => {
    setGuests(prev => prev.map(guest => 
      guest.id === guestId 
        ? { ...guest, cardId, floor, elevatorZone }
        : guest
    ));

    // Add realtime activity
    const guest = guests.find(g => g.id === guestId);
    if (guest) {
      setRealtimeActivity(prev => [{
        id: generateId(),
        guestName: guest.fullName,
        action: 'check-in' as const,
        time: new Date(),
        floor: floor
      }, ...prev.slice(0, 19)]);

      addNotification({
        type: 'approval-needed',
        title: 'Cấp thẻ thành công',
        message: `Đã cấp thẻ ${cardId} cho ${guest.fullName}`,
        guestId
      });
    }
  };

  // Check in guest
  const checkInGuest = (guestId: string) => {
    const guest = guests.find(g => g.id === guestId);
    if (!guest) return;

    setGuests(prev => prev.map(g => 
      g.id === guestId 
        ? { ...g, checkInTime: new Date() }
        : g
    ));

    // Add realtime activity
    setRealtimeActivity(prev => [{
      id: generateId(),
      guestName: guest.fullName,
      action: 'check-in' as const,
      time: new Date(),
      floor: guest.floor
    }, ...prev.slice(0, 19)]);

    addNotification({
      type: 'approval-needed',
      title: 'Check-in thành công',
      message: `${guest.fullName} đã vào tòa nhà`,
      guestId
    });
  };

  // Check out guest
  const checkOutGuest = (guestId: string) => {
    const guest = guests.find(g => g.id === guestId);
    if (!guest) return;

    setGuests(prev => prev.map(g => 
      g.id === guestId 
        ? { ...g, checkOutTime: new Date() }
        : g
    ));

    // Add realtime activity
    setRealtimeActivity(prev => [{
      id: generateId(),
      guestName: guest.fullName,
      action: 'check-out' as const,
      time: new Date(),
      floor: guest.floor
    }, ...prev.slice(0, 19)]);

    addNotification({
      type: 'approval-needed',
      title: 'Check-out thành công',
      message: `${guest.fullName} đã ra khỏi tòa nhà`,
      guestId
    });
  };

  // Add notification
  const addNotification = (notificationData: Omit<NotificationItem, 'id' | 'time' | 'read'>) => {
    const newNotification: NotificationItem = {
      ...notificationData,
      id: generateId(),
      time: new Date(),
      read: false
    };

    setNotifications(prev => [newNotification, ...prev.slice(0, 19)]);
  };

  // Mark notification as read
  const markNotificationAsRead = (notificationId: string) => {
    setNotifications(prev => prev.map(notification => 
      notification.id === notificationId 
        ? { ...notification, read: true }
        : notification
    ));
  };

  // Reset all data to initial mock data (for demo purposes)
  const resetToMockData = () => {
    setGuests(mockGuests);
    setStatistics(mockStatistics);
    setRealtimeActivity(mockRealtimeActivity);
    setNotifications([
      {
        id: '1',
        type: 'new-registration',
        title: 'Đăng ký mới',
        message: 'Lê Thị Bình đã đăng ký vào tòa nhà',
        time: new Date(),
        read: false,
        guestId: '2'
      },
      {
        id: '2',
        type: 'approval-needed',
        title: 'Cần duyệt',
        message: 'Phạm Thị Dung chờ duyệt đăng ký',
        time: new Date(),
        read: false,
        guestId: '4'
      }
    ]);
    
    // Clear localStorage
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
    
    console.log('Data reset to mock data');
  };

  // Update statistics based on current data
  useEffect(() => {
    const currentInBuilding = guests.filter(g => g.checkInTime && !g.checkOutTime).length;
    const todayCheckIns = guests.filter(g => g.checkInTime && 
      new Date(g.checkInTime).toDateString() === new Date().toDateString()
    ).length;
    const todayCheckOuts = guests.filter(g => g.checkOutTime && 
      new Date(g.checkOutTime).toDateString() === new Date().toDateString()
    ).length;

    // Update company stats
    const companyStats = guests
      .filter(g => g.checkInTime && g.company)
      .reduce((acc, guest) => {
        acc[guest.company] = (acc[guest.company] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

    const topCompanies = Object.entries(companyStats)
      .map(([company, count]) => ({ company, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Update floor stats
    const floorStats = guests
      .filter(g => g.checkInTime && g.floor)
      .reduce((acc, guest) => {
        acc[guest.floor!] = (acc[guest.floor!] || 0) + 1;
        return acc;
      }, {} as Record<number, number>);

    const floorBreakdown = Object.entries(floorStats)
      .map(([floor, count]) => ({ floor: parseInt(floor), count }))
      .sort((a, b) => a.floor - b.floor);

    setStatistics(prev => ({
      ...prev,
      currentInBuilding,
      todayCheckIns,
      todayCheckOuts,
      topCompanies,
      floorBreakdown
    }));
  }, [guests]);

  const value: AppContextType = {
    guests,
    statistics,
    realtimeActivity,
    notifications,
    addGuest,
    approveGuest,
    rejectGuest,
    issueCard,
    checkInGuest,
    checkOutGuest,
    markNotificationAsRead,
    addNotification,
    resetToMockData
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};